import { messageNotifies } from '@hive/shared';
import type { Message, NotifyLevel } from '@/data/comms';

// Which new messages ring a desktop notification, worked out without the
// browser so it's unit-tested. The hook in useCommsNotifications applies it.

export interface NotifySituation {
  meId: string;
  /** The member's setting for the message's channel (default all). */
  notify: NotifyLevel;
  /** The channel this window shows, if it's on Comms. */
  viewingChannelId: string | null;
  /** Whether this window has focus. */
  focused: boolean;
  /** Whether this window is the one that rings (the most recently focused). */
  notifier: boolean;
}

export type NotifyMessage = Pick<
  Message,
  'channel_id' | 'author_id' | 'author_kind' | 'on_behalf_of' | 'body' | 'deleted_at' | 'source'
>;

export function shouldNotify(message: NotifyMessage, s: NotifySituation): boolean {
  if (!s.notifier) return false;
  // Nothing rings for the channel you're looking at.
  if (s.focused && s.viewingChannelId === message.channel_id) return false;
  // The rest (your own posts, imported ones, channel settings) is shared with phone push.
  return messageNotifies(message, s.meId, s.notify);
}

export { notificationText } from '@hive/shared';

// The most recently focused window rings; the others stay quiet. Windows of
// the same app share localStorage, so the last one to take focus writes its id.
const NOTIFIER_KEY = 'hive.comms.notifier';
const windowId = Math.random().toString(36).slice(2);

export function claimNotifier(): void {
  try {
    localStorage.setItem(NOTIFIER_KEY, windowId);
  } catch {
    // No storage: every window rings, which beats none.
  }
}

export function releaseNotifier(): void {
  try {
    if (localStorage.getItem(NOTIFIER_KEY) === windowId) localStorage.removeItem(NOTIFIER_KEY);
  } catch {
    // As above.
  }
}

export function isNotifier(): boolean {
  try {
    const current = localStorage.getItem(NOTIFIER_KEY);
    return !current || current === windowId;
  } catch {
    return true;
  }
}

/** The sidebar's Comms badge: unread across channels not set to none, plus any mention. */
export function commsBadge(
  unread: readonly { channel_id: string; unread: number; mentions: number }[],
  settings: readonly { channel_id: string; notify: string }[],
): { count: number; mention: boolean } {
  let count = 0;
  let mention = false;
  for (const row of unread) {
    const muted = settings.find((s) => s.channel_id === row.channel_id)?.notify === 'none';
    if (!muted) count += row.unread;
    else count += row.mentions;
    if (row.mentions > 0) mention = true;
  }
  return { count, mention };
}
